if (!customElements.get('heating-bundles-no')) {
  customElements.define('heating-bundles-no', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      // A section can reconnect in the theme editor; its form is a later sibling.
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || !this.initialized || this.listenersBound) return;
      this.productInfo = this.closest('product-info') || this.closest('[data-heating-product]') || this.parentElement;
      if (this.dataset.productId !== '12413793894664' || this.productInfo?.dataset.productId !== '12413793894664' || this.productInfo?.dataset.heatingBundles !== 'true') return;
      this.form = document.getElementById(this.dataset.formId);
      this.errorNode = this.querySelector('[data-heating-error]');
      this.packIndex = Number(this.dataset.packIndex ?? -1);
      this.colorIndex = Number(this.dataset.colorIndex);
      this.sizeIndex = Number(this.dataset.sizeIndex);
      const requestedId = new URL(window.location.href).searchParams.get('variant') || this.dataset.selectedId;
      let requestedVariant;
      try {
        this.variants = JSON.parse(this.querySelector('[data-heating-variants]').textContent);
        this.bankVariants = JSON.parse(this.querySelector('[data-heating-bank-variants]').textContent);
        if (!this.form || !this.productInfo.contains(this.form) || !Array.isArray(this.variants)) throw new Error('Missing vest options');
        requestedVariant = this.variants.find(variant => String(variant.id) === requestedId)
          || this.variants.find(variant => String(variant.id) === this.dataset.selectedId);
        this.variants = this.variants.filter(variant => this.isSingleVariant(variant) && this.isAllowedSize(variant.options[this.sizeIndex]));
        if (!this.variants.length) throw new Error('Missing single vest options');
      } catch (error) {
        this.showError('Vestvariantene kunne ikke lastes inn. Oppdater siden og prøv igjen.');
        const submit = this.form?.querySelector('[type="submit"]');
        if (submit) submit.disabled = true;
        return;
      }
      this.cards = [...this.querySelectorAll('[data-heating-card]')];
      this.bankToggle = this.querySelector('[data-heating-bank-toggle]');
      this.bankSelect = this.querySelector('[data-heating-bank-variant]');
      this.bankQuantitySelect = this.querySelector('[data-heating-bank-quantity]');
      this.bankOptions = this.querySelector('[data-heating-bank-options]');
      this.dialog = this.querySelector('dialog');
      this.formatter = new Intl.NumberFormat('nb-NO', {
        style: 'currency', currency: this.dataset.currency || 'NOK',
      });
      const initial = this.variants.find(variant => variant.id === requestedVariant?.id)
        || this.fallbackVariant(requestedVariant?.options[this.colorIndex]);
      this.initialSelection = { color: initial.options[this.colorIndex], size: initial.options[this.sizeIndex] };
      this.rowsByQuantity = new Map(this.cards.map(card => [Number(card.dataset.quantity),
        Array.from({ length: Number(card.dataset.quantity) }, () => ({ ...this.initialSelection })),
      ]));
      this.quantity = 2;
      this.focusedIndex = 0;
      this.submitting = false;
      this.hasExplicitColorSelection = false;
      this.onChange = this.handleChange.bind(this);
      this.onFocus = this.handleFocus.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onReset = this.handleReset.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('focusin', this.onFocus);
      this.addEventListener('click', this.onClick);
      this.form.addEventListener('submit', this.onSubmit, true);
      this.form.addEventListener('reset', this.onReset);
      this.listenersBound = true;
      // Initialize offer controls only. The server-rendered product hero stays
      // active until the customer explicitly chooses a color.
      this.render(requestedId !== String(initial.id));
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('focusin', this.onFocus);
      this.removeEventListener('click', this.onClick);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.form?.removeEventListener('reset', this.onReset);
      if (this.dialog?.open) this.dialog.close();
      this.listenersBound = false;
      this.initialized = false;
    }

    money(cents) { return this.formatter.format(Number(cents || 0) / 100); }
    rows() { return this.rowsByQuantity.get(this.quantity) || []; }
    focusedRow() { return this.rows()[this.focusedIndex] || this.rows()[0]; }
    isSingleVariant(variant) {
      if (this.packIndex < 0) return true;
      return /^(?:1(?:pieces?|pack|vests?)?|single)$/.test(String(variant.options[this.packIndex] || '').toLowerCase().replace(/[\s_-]/g, ''));
    }
    isAllowedSize(size) { return /^(?:S|M|L|XL|2XL|3XL|4XL)$/.test(String(size || '').trim().toUpperCase()); }
    fallbackVariant(color) {
      // Prefer available sizes, preserving the requested color and starting at S.
      return this.variants.reduce((best, variant) => {
        const score = candidate => (candidate.available ? 100 : 0)
          + (candidate.options[this.colorIndex] === color ? 20 : 0)
          + (String(candidate.options[this.sizeIndex]).trim().toUpperCase() === 'S' ? 10 : 0);
        return !best || score(variant) > score(best) ? variant : best;
      }, null);
    }
    variantFor(row) {
      return row && this.isAllowedSize(row.size) && this.variants.find(variant => this.isSingleVariant(variant) && variant.options[this.colorIndex] === row.color && variant.options[this.sizeIndex] === row.size);
    }
    selectedBank() { return this.bankVariants.find(variant => String(variant.id) === this.bankSelect?.value); }
    selectedBankQuantity() {
      const quantity = Number(this.bankQuantitySelect?.value ?? 1);
      return Number.isInteger(quantity) && quantity >= 1 && quantity <= 3 ? quantity : null;
    }
    bankDiscountPercent(quantity) {
      const percent = quantity === 2 ? this.dataset.bankTwoDiscountPercent
        : quantity === 3 ? this.dataset.bankThreeDiscountPercent : 0;
      return Math.min(100, Math.max(0, Number(percent) || 0));
    }
    bankTotal(bank, quantity) {
      const percent = this.bankDiscountPercent(quantity);
      return this.discountedUnitPrice(bank?.price, percent) * quantity;
    }
    discountedUnitPrice(price, percent) {
      const cents = Number(price || 0);
      // Shopify floors the discount on each unit before subtracting it.
      // A tiny epsilon prevents binary decimals from losing an exact cent.
      const discount = Math.floor(cents * percent / 100 + 1e-8);
      return cents - discount;
    }
    swatch(color) { return /navy|blue/i.test(color || '') ? '#182641' : '#1a1b1f'; }

    handleFocus(event) {
      const rowNode = event.target.closest?.('[data-heating-row]');
      if (!rowNode || Number(rowNode.closest('[data-heating-card]').dataset.quantity) !== this.quantity) return;
      this.focusedIndex = Number(rowNode.dataset.index) || 0;
      this.renderPreview();
    }

    handleChange(event) {
      const target = event.target;
      let changedColor = false;
      if (target.matches('[data-heating-pack]')) {
        this.quantity = Number(target.value);
        this.focusedIndex = 0;
      } else if (target.matches('[data-heating-preview-color]')) {
        this.focusedRow().color = target.value;
        changedColor = true;
      } else if (target.matches('[data-heating-color], [data-heating-size]')) {
        const card = target.closest('[data-heating-card]');
        if (Number(card.dataset.quantity) !== this.quantity) return;
        this.focusedIndex = Number(target.closest('[data-heating-row]').dataset.index) || 0;
        const row = this.focusedRow();
        if (target.matches('[data-heating-color]')) {
          row.color = target.value;
          changedColor = true;
        } else {
          if (!this.isAllowedSize(target.value)) {
            this.render(false);
            this.showError('Velg en størrelse fra S til 4XL.');
            return;
          }
          row.size = target.value;
        }
      } else if (!target.matches('[data-heating-bank-toggle], [data-heating-bank-variant], [data-heating-bank-quantity]')) return;
      this.showError();
      this.render(true);
      if (changedColor) {
        this.hasExplicitColorSelection = true;
        this.updateMedia(this.variantFor(this.focusedRow()));
      }
    }

    handleClick(event) {
      if (event.target.closest('[data-heating-size-open]')) {
        this.dialog?.showModal();
      } else if (event.target.closest('[data-heating-size-close]') || event.target === this.dialog) {
        this.dialog?.close();
      } else if (event.target.matches('[data-heating-preview-color]') && !event.target.disabled) {
        // Clicking an already selected swatch still reveals its existing photo.
        this.hasExplicitColorSelection = true;
        this.updateMedia(this.variantFor({ ...this.focusedRow(), color: event.target.value }));
      }
    }

    handleReset() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        this.rowsByQuantity.forEach(rows => rows.forEach(row => Object.assign(row, this.initialSelection)));
        this.quantity = 2;
        this.focusedIndex = 0;
        this.bankToggle.checked = false;
        if (this.bankQuantitySelect) this.bankQuantitySelect.value = '1';
        if (this.bankSelect) this.bankSelect.value = String(this.bankVariants.find(variant => variant.available)?.id || '');
        this.showError();
        this.render(true);
        this.updateMedia(this.variantFor(this.focusedRow()));
      });
    }

    colorName(color) {
      return ({ Black: 'Svart', 'Navy Blue': 'Marineblå' })[color] || color;
    }

    renderPreview() {
      const row = this.focusedRow();
      const label = this.querySelector('[data-heating-selected-color]');
      label.textContent = this.colorName(row.color) + (this.quantity > 1 ? ` — Vest ${this.focusedIndex + 1}` : '');
      this.querySelectorAll('[data-heating-preview-color]').forEach(input => {
        input.checked = input.value === row.color;
        input.disabled = !this.variants.some(variant => variant.available && variant.options[this.colorIndex] === input.value);
      });
    }

    render(updateUrl) {
      this.cards.forEach(card => {
        const quantity = Number(card.dataset.quantity);
        const rows = this.rowsByQuantity.get(quantity);
        const active = quantity === this.quantity;
        const available = rows.every(row => this.variantFor(row)?.available);
        const discountPercent = Number(card.dataset.discountPercent) || 0;
        // Match the automatic Shopify quantity discount using real vest prices.
        // Power-bank quantity discounts are calculated separately below.
        const total = rows.reduce((sum, row) => sum + this.discountedUnitPrice(this.variantFor(row)?.price, discountPercent), 0);
        const comparePrices = rows.map(row => Number(this.variantFor(row)?.compare_at_price || 0));
        const compareTotal = comparePrices.every(price => price > 0) ? comparePrices.reduce((sum, price) => sum + price, 0) : 0;
        card.classList.toggle('is-selected', active);
        card.classList.toggle('is-unavailable', !available);
        card.querySelector('[data-heating-pack]').checked = active;
        card.querySelector('[data-heating-selectors]').hidden = !active;
        card.querySelector('[data-heating-availability]').hidden = available;
        card.querySelector('[data-heating-price]').textContent = total == null ? '—' : this.money(total);
        const compare = card.querySelector('[data-heating-compare]');
        compare.hidden = !(compareTotal > total);
        compare.textContent = this.money(compareTotal);
        const each = card.querySelector('[data-heating-each]');
        if (each) each.textContent = `${this.money(total / quantity)} per stk.`;
        const saving = card.querySelector('[data-heating-saving]');
        if (saving) {
          saving.hidden = !(compareTotal > total);
          saving.textContent = compareTotal > total ? `SPAR ${Math.round((compareTotal - total) / compareTotal * 100)}%` : '';
        }
        card.querySelectorAll('[data-heating-row]').forEach(rowNode => {
          const row = rows[Number(rowNode.dataset.index)];
          const colorSelect = rowNode.querySelector('[data-heating-color]');
          const sizeSelect = rowNode.querySelector('[data-heating-size]');
          [...sizeSelect.options].forEach(option => {
            if (!this.isAllowedSize(option.value)) option.remove();
          });
          colorSelect.value = row.color;
          sizeSelect.value = row.size;
          rowNode.querySelector('[data-heating-color-dot]').style.backgroundColor = this.swatch(row.color);
          [...colorSelect.options].forEach(option => {
            option.disabled = !this.variants.some(variant => variant.available && variant.options[this.colorIndex] === option.value);
          });
          [...sizeSelect.options].forEach(option => {
            const variant = this.variantFor({ color: row.color, size: option.value });
            option.disabled = !variant?.available;
            option.textContent = option.value + (variant?.available ? '' : ' — Utsolgt');
          });
        });
      });
      this.renderPreview();
      const bank = this.selectedBank();
      const bankQuantity = this.selectedBankQuantity();
      const bankEnabled = this.bankToggle.checked;
      if (this.bankOptions) this.bankOptions.hidden = !bankEnabled;
      this.bankToggle.setAttribute('aria-expanded', String(bankEnabled));
      if (this.bankSelect) this.bankSelect.disabled = !bankEnabled;
      if (this.bankQuantitySelect) this.bankQuantitySelect.disabled = !bankEnabled;
      const displayedBankQuantity = bankEnabled ? bankQuantity || 1 : 1;
      const bankSubtotal = Number(bank?.price || 0) * displayedBankQuantity;
      const bankTotal = this.bankTotal(bank, displayedBankQuantity);
      const bankSavingPercent = this.bankDiscountPercent(displayedBankQuantity);
      this.querySelector('[data-heating-bank-price]').textContent = bank ? `+ ${this.money(bankTotal)}` : '';
      const bankCompare = this.querySelector('[data-heating-bank-compare]');
      if (bankCompare) {
        bankCompare.textContent = this.money(bankSubtotal);
        bankCompare.hidden = !bank || !(bankSubtotal > bankTotal);
      }
      const bankSaving = this.querySelector('[data-heating-bank-saving]');
      if (bankSaving) {
        bankSaving.textContent = bankSavingPercent ? `Spar ${bankSavingPercent}%` : '';
        bankSaving.hidden = !bank || !(bankSubtotal > bankTotal);
      }
      const valid = !!this.getCartItems();
      const firstVariant = this.variantFor(this.rows()[0]);
      this.renderUnitPrice(firstVariant);
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) {
        idInput.value = firstVariant ? String(firstVariant.id) : '';
        idInput.disabled = !valid;
      }
      const quantityInput = this.form.querySelector('[name="quantity"]');
      if (quantityInput) quantityInput.value = '1';
      const submit = this.form.querySelector('[type="submit"]');
      if (submit) {
        submit.disabled = this.submitting || !valid;
        const label = submit.querySelector('span');
        if (label) label.textContent = valid ? (this.dataset.addToCart || 'Legg i handlekurven') : 'Valgt variant er utsolgt';
      }
      if (updateUrl && firstVariant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', firstVariant.id);
        window.history.replaceState({}, '', url);
      }
      this.querySelector('[data-heating-status]').textContent = valid
        ? `${this.quantity} ${this.quantity === 1 ? 'vest valgt' : 'vester valgt'}. ${this.rows().map((row, index) => `Vest ${index + 1}: ${this.colorName(row.color)}, ${row.size}`).join('; ')}.${bankEnabled ? ` ${bankQuantity} ${bankQuantity === 1 ? 'powerbank' : 'powerbanker'}: ${bank.title}.` : ' Powerbank er ikke inkludert.'}`
        : 'En valgt variant er utsolgt. Velg en annen farge eller størrelse.';
    }

    renderUnitPrice(variant) {
      if (!variant) return;
      const price = Number(variant.price || 0);
      const compare = Number(variant.compare_at_price || 0);
      const onSale = compare > price && compare > 0;
      // The main price describes one vest. Bundle savings stay on their cards.
      this.productInfo.querySelectorAll('[data-heating-product-price]').forEach(container => {
        container.classList.toggle('price--on-sale', onSale);
        container.classList.toggle('price--sold-out', !variant.available);
        container.querySelectorAll('[data-heating-regular-price], [data-heating-sale-price]').forEach(node => {
          node.textContent = this.money(price);
        });
        const compareNode = container.querySelector('[data-heating-compare-price]');
        if (compareNode) {
          compareNode.textContent = onSale ? this.money(compare) : '';
          compareNode.hidden = !onSale;
        }
        const badge = container.querySelector('[data-heating-sale-badge]');
        if (badge) {
          badge.hidden = !onSale;
          badge.classList.toggle('hidden', !onSale);
          badge.textContent = onSale ? `Spar ${Math.round((compare - price) / compare * 100)}%` : '';
        }
      });
    }

    updateMedia(variant) {
      if (!this.hasExplicitColorSelection) return;
      const color = this.focusedRow()?.color;
      const mediaId = variant?.media_id || this.variants.find(candidate => candidate.options[this.colorIndex] === color && candidate.media_id)?.media_id;
      const gallery = this.productInfo.querySelector('media-gallery');
      const targetId = `${this.dataset.sectionId}-${mediaId}`;
      if (!mediaId || !gallery?.querySelector(`[data-media-id="${targetId}"]`)) return;
      // Reuse native gallery behavior without moving the customer up the page.
      gallery.setActiveMedia?.(targetId, false, { scrollIntoView: false });
    }

    getCartItems() {
      const items = new Map();
      for (const row of this.rows()) {
        const variant = this.variantFor(row);
        if (!variant?.available) return null;
        const key = String(variant.id);
        if (items.has(key)) items.get(key).quantity += 1;
        else items.set(key, { id: variant.id, quantity: 1 });
      }
      if (!items.size) return null;
      if (this.bankToggle.checked) {
        const bank = this.selectedBank();
        const quantity = this.selectedBankQuantity();
        if (!bank?.available || !quantity) return null;
        items.set(`bank-${bank.id}`, { id: bank.id, quantity });
      }
      return [...items.values()];
    }

    showError(message = '') {
      if (!this.errorNode) return;
      this.errorNode.textContent = message;
      this.errorNode.hidden = !message;
    }

    async handleSubmit(event) {
      // Capture before Dawn's normal single-variant handler. Prices are resolved
      // by Shopify; this request never attempts to set a client-side line price.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.submitting) return;
      const items = this.getCartItems();
      if (!items) {
        this.showError('En valgt variant er utsolgt. Velg en annen farge eller størrelse.');
        this.errorNode?.focus();
        return;
      }
      const submit = this.form.querySelector('[type="submit"]');
      const productForm = this.form.closest('product-form');
      const spinner = productForm?.querySelector('.loading__spinner');
      this.submitting = true;
      submit.disabled = true;
      submit.setAttribute('aria-disabled', 'true');
      submit.classList.add('loading');
      spinner?.classList.remove('hidden');
      this.showError();
      productForm?.handleErrorMessage?.();
      let added = false;
      try {
        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Varene kunne ikke legges i handlekurven. Prøv igjen.');
        added = true;
        window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
      } catch (error) {
        if (!this.isConnected) return;
        this.showError(error.message || 'Varene kunne ikke legges i handlekurven. Prøv igjen.');
      } finally {
        if (!added && this.isConnected) {
          this.submitting = false;
          submit.classList.remove('loading');
          submit.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
          submit.disabled = !this.getCartItems();
        }
      }
    }
  });
}
